/**
 * Тик года (DESIGN §4.3): один прожитый год как чистая функция
 * `advanceYear(ds, life, input, rng) → { life, entries, pendingDecision }`.
 *
 * Порядок шагов:
 *   0a) отложенная ставка прошлого года (`flags.betPending`) разрешается;
 *   0b) вложения и займы прошлых лет (пирамида, монета, акции, заём)
 *      обслуживаются до мини-игры этого года (план 2b, Task 4);
 *   0c) мини-игра года `input.minigame`: исход (деньги, состояния, флаги,
 *      запись) применяется до решения и до экономики — так игра при решении
 *      (экзамен, собеседование) успевает поставить флаги, которые решение
 *      читает (`flags.interviewLevel`); перед игрой — спад тяги −4 за
 *      прошедший год (всегда, и в год с игрой); при тяге ≥ 60 состояние
 *      `gambling`: счастье −3 и запись `urge.offer`;
 *   0d) действия года на энергию `input.actions` (план 2d, energy.ts):
 *      по порядку, при нулевой энергии или третьем визите в место —
 *      отбрасываются; второй визит даёт половину;
 *   0) висящее решение прошлого года: ответ игрока (`input.answer`) или
 *      автовыбор, если ответа нет, он к другому решению либо отвергнут
 *      движком (недоступный вариант «починенного» клиента); при автовыборе
 *      варианты «ничего не изменилось» в журнал не пишутся;
 *   1) фокус: `input.focus`, если он доступен в этом возрасте, иначе автопилот;
 *      при `input.restless` перед фокусом счастье −1 (метания игрока);
 *      `flags.hobbyYears` считает годы хобби подряд (план 2b, F6);
 *   2) погашение долга `input.payDebt` (min с деньгами и долгом), затем
 *      экономика (с переводами `transfersIn`);
 *   3) здоровье;
 *   4) смерть — при смерти год заканчивается здесь;
 *   5) событие года (после тяжёлого события `help.lines` уже в записях);
 *   5a) возраст младшего ребёнка (`flags.youngestAge`): 0 при новом ребёнке, иначе +1;
 *   5b) досье H1 (look.ts): дети старше на год, второй родитель уходит молча,
 *      жильё (rent при работе/переезде, own после 30 с деньгами — запись
 *      `assets.housing.own`), внешность (рост до 18, седина после 50);
 *   5c) цели (goals.ts): мечта сбылась (`dreamProgress` ≥ 1 → `achievedAt`,
 *      запись `dream.achieved`, счастье), цель года выполнена (запись
 *      `goal.done`, счастье +2), вехи (`checkMilestones` против жизни на
 *      начало года — помеченные записи `milestone.*`);
 *   6) возраст +1, год учёбы; энергия нового года (`energyFor`), визиты — пусто;
 *   6a) в DREAM_AGE — предложение мечт `pendingDream` (ответ — `input.dream`
 *      следующего года, без ответа автопилот берёт первую; `input.noDream`
 *      — жить без мечты); новая цель года, если прежняя выполнена или её нет;
 *   7) решение, наступившее в новом возрасте → `pendingDecision`;
 *   8) запись `year.summary`.
 * `flags.skipHeavy` берётся из `input.skipHeavy`, если он задан, иначе хранится.
 *
 * Каждая подсистема получает свой `rng.fork(...)`, поэтому добавление
 * жребия в одной не сдвигает случайности другой. Вход не мутируется:
 * журнал — новый массив, записи года возвращаются и отдельно (`entries`).
 */
import { getCountry, type Dataset } from '@life/data';
import { t } from '@life/i18n';
import { chooseFocus, chooseOption } from './autopilot';
import { checkDeath } from './death';
import { dueDecision, resolveDecision } from './decisions/index';
import { applyEconomy } from './economy';
import { applyEvent } from './events/index';
import { applyFocus, availableFocus, clampStats, focusDef } from './focus';
import { applyHealth } from './health';
import { serviceHoldings } from './holdings';
import { HOUSING_OWN_KEY, ageChildren, growLook, loseSecondParent, updateAssets } from './look';
import { applyActions, energyFor, type ActionInput } from './energy';
import { DREAM_AGE, achieveDream, offerDreams, resolveDream, settleGoal, stampMilestones, yearGoalFor } from './goals';
import { applyMinigame, applyUrgeYear, decayUrge, resolveBet, type MinigameOutcome } from './minigames';
import type { Rng } from './rng';
import type { Decision, DreamId, Focus, Life, LogEntry } from './types';

export interface YearInput {
  focus?: Focus;
  answer?: { decisionId: string; optionId: string };
  /** Переводы от других игроков за год, доллары ППС, ≥ 0. */
  transfersIn?: number;
  skipHeavy?: boolean;
  /** Игрок дёргал фокус больше трёх раз за год: −1 счастья перед фокусом. */
  restless?: boolean;
  /** Сколько долга погасить до экономики; реально гасится min(payDebt, money, debt). */
  payDebt?: number;
  /** Сыгранная за год мини-игра: исход применяется до экономики (план 2b). */
  minigame?: { id: string; outcome: MinigameOutcome };
  /** Действия года по местам (план 2d): применяются после мини-игры, до решения и фокуса; лишние отбрасываются. */
  actions?: ActionInput[];
  /** Выбор мечты из `life.pendingDream`; без ответа автопилот берёт первую. */
  dream?: DreamId | undefined;
  /** Жить без мечты: предложение снимается, мечта не ставится (режим наблюдения, тесты детерминизма). */
  noDream?: boolean;
}

export interface YearResult {
  life: Life;
  /** Записи журнала этого года в порядке появления (уже добавлены в `life.log`). */
  entries: LogEntry[];
  pendingDecision: Decision | null;
}

/** Ключ итоговой записи года в каталоге i18n. */
export const YEAR_SUMMARY_KEY = 'year.summary';
export const FLAG_SKIP_HEAVY = 'skipHeavy';
/** Сколько лет подряд фокус «хобби» (мини-игра F6 «уличная музыка» с трёх лет); сбрасывается другим фокусом. */
export const FLAG_HOBBY_YEARS = 'hobbyYears';

/**
 * Возраст младшего ребёнка (мини-игры G6 «ночь» и G7 «разговор»): 0 в год, когда
 * детей стало больше (решение family.child или событие child.born), дальше +1 в год.
 * Без детей флага нет.
 */
export const FLAG_YOUNGEST_AGE = 'youngestAge';

/** Шаг 5a: возраст младшего ребёнка — 0 при новом ребёнке за год, иначе +1, если флаг есть. */
function withYoungestAge(life: Life, childrenAtStart: number): Life {
  if (life.children > childrenAtStart) return { ...life, flags: { ...life.flags, [FLAG_YOUNGEST_AGE]: 0 } };
  const raw = life.flags[FLAG_YOUNGEST_AGE];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return life;
  return { ...life, flags: { ...life.flags, [FLAG_YOUNGEST_AGE]: raw + 1 } };
}

/** Счётчик лет хобби подряд: +1 при фокусе `hobby`, иначе флаг снимается. */
function withHobbyYears(life: Life, focus: Focus): Life {
  const raw = life.flags[FLAG_HOBBY_YEARS];
  const prev = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
  if (focus === 'hobby') return { ...life, flags: { ...life.flags, [FLAG_HOBBY_YEARS]: prev + 1 } };
  if (prev === 0 && !(FLAG_HOBBY_YEARS in life.flags)) return life;
  const flags: Life['flags'] = {};
  for (const [k, v] of Object.entries(life.flags)) if (k !== FLAG_HOBBY_YEARS) flags[k] = v;
  return { ...life, flags };
}

function withSkipHeavy(life: Life, skipHeavy: boolean | undefined): Life {
  if (skipHeavy === undefined) return life;
  return { ...life, flags: { ...life.flags, [FLAG_SKIP_HEAVY]: skipHeavy } };
}

/** Шаг 0: применить ответ на висящее решение либо автовыбор. */
function settlePending(ds: Dataset, life: Life, input: YearInput, rng: Rng): { life: Life; entries: LogEntry[] } {
  const decision = life.pendingDecision;
  if (decision === null) return { life, entries: [] };
  const answer = input.answer;
  if (answer && answer.decisionId === decision.id) {
    const answered = resolveDecision(ds, life, decision, answer.optionId, rng);
    if (!answered.rejected) return answered;
  }
  const fallback = resolveDecision(ds, life, decision, chooseOption(life, decision), rng, { auto: true });
  if (fallback.rejected) throw new RangeError(`advanceYear: decision ${decision.id} cannot be resolved`);
  return fallback;
}

/** Шаг 0e: предложенная мечта — ответ игрока, отказ (`noDream`) или первая из списка (автопилот). */
function settleDream(life: Life, input: YearInput): { life: Life; entries: LogEntry[] } {
  const offered = life.pendingDream;
  if (offered === null) return { life, entries: [] };
  if (offered.length === 0 || input.noDream) return { life: { ...life, pendingDream: null }, entries: [] };
  if (input.dream !== undefined && offered.includes(input.dream)) return resolveDream(life, input.dream);
  return resolveDream(life, offered[0]!);
}

/** Непоседливость: −1 счастья до эффектов фокуса. */
export const RESTLESS_HAPPINESS = -1;

function withRestless(life: Life, restless: boolean | undefined): Life {
  if (!restless) return life;
  return { ...life, stats: clampStats({ ...life.stats, happiness: life.stats.happiness + RESTLESS_HAPPINESS }) };
}

/** Погашение долга из денег: не больше, чем есть денег и долга; отрицательное и NaN — ноль. */
function withDebtPaid(life: Life, amount: number | undefined): Life {
  if (amount === undefined || !(amount > 0)) return life;
  const paid = Math.min(amount, Math.max(0, life.money), Math.max(0, life.debt));
  if (paid <= 0) return life;
  return { ...life, money: life.money - paid, debt: life.debt - paid };
}

/** Шаг 1: фокус из входа, если доступен, иначе автопилот. */
function pickFocus(life: Life, input: YearInput): Focus {
  if (input.focus !== undefined && availableFocus(life).includes(input.focus)) return input.focus;
  return chooseFocus(life);
}

function yearSummary(life: Life, focus: Focus): LogEntry {
  return {
    age: life.age,
    key: YEAR_SUMMARY_KEY,
    params: {
      age: life.age,
      year: life.birthYear + life.age,
      focus: t(focusDef(focus).labelKey),
      health: Math.round(life.stats.health),
      happiness: Math.round(life.stats.happiness),
      knowledge: Math.round(life.stats.knowledge),
      relations: Math.round(life.stats.relations),
    },
  };
}

function finish(life: Life, entries: LogEntry[]): YearResult {
  const next: Life = { ...life, log: [...life.log, ...entries] };
  return { life: next, entries, pendingDecision: next.pendingDecision };
}

/** Один год жизни. Мёртвая жизнь возвращается как есть, без записей. */
export function advanceYear(ds: Dataset, life: Life, input: YearInput, rng: Rng): YearResult {
  if (!life.alive) return { life, entries: [], pendingDecision: null };

  const entries: LogEntry[] = [];
  let current = withSkipHeavy(life, input.skipHeavy);

  // 0a) Ставка прошлого года: выигрыш или проигрыш известен в начале года.
  const bet = resolveBet(current, rng.fork('bet'));
  current = bet.life;
  entries.push(...bet.entries);

  // 0b) Пирамида, монета, акции и заём прошлых лет — до мини-игры этого года.
  const holdings = serviceHoldings(current, rng.fork('holdings'));
  current = holdings.life;
  entries.push(...holdings.entries);

  // 0c) Спад тяги за прошедший год, затем мини-игра года (исход до решения и до экономики) и годовой эффект состояния gambling.
  current = decayUrge(current);
  if (input.minigame) {
    const played = applyMinigame(current, input.minigame.outcome);
    current = played.life;
    entries.push(...played.entries);
  }
  // 0d) Действия года на энергию (места): по порядку, лишние отбрасываются.
  if (input.actions && input.actions.length > 0) {
    const acted = applyActions(current, input.actions);
    current = acted.life;
    entries.push(...acted.entries);
  }
  const urge = applyUrgeYear(current);
  current = urge.life;
  entries.push(...urge.entries);

  // 0) Решение прошлого года.
  const settled = settlePending(ds, current, input, rng.fork('answer'));
  current = settled.life;
  entries.push(...settled.entries);

  // 0e) Предложенная мечта: ответ, отказ или первая.
  const dreamed = settleDream(current, input);
  current = dreamed.life;
  entries.push(...dreamed.entries);

  // 1) Фокус года.
  const focus = pickFocus(current, input);
  current = withHobbyYears(applyFocus({ ...withRestless(current, input.restless), focus }, rng.fork('focus')), focus);

  // 2) Погашение долга и экономика.
  current = withDebtPaid(current, input.payDebt);
  const economy = applyEconomy(ds, current, rng.fork('economy'), input.transfersIn ?? 0);
  current = economy.life;
  entries.push(...economy.entries);

  // 3) Здоровье.
  const health = applyHealth(ds, current, rng.fork('health'));
  current = health.life;
  entries.push(...health.entries);

  // 4) Смерть: год заканчивается.
  const death = checkDeath(ds, current, rng.fork('death'));
  if (!death.life.alive) {
    entries.push(...death.entries);
    return finish({ ...death.life, pendingDecision: null }, entries);
  }

  // 5) Событие года.
  const event = applyEvent({ ds, life: current, year: current.birthYear + current.age }, rng.fork('event'));
  current = event.life;
  entries.push(...event.entries);

  // 5a) Возраст младшего ребёнка: 0 при рождении в этом году, иначе +1.
  current = withYoungestAge(current, life.children);

  // 5b) Досье: дети, родители, жильё, внешность. Свои fork'и — калибровку не сдвигают.
  current = ageChildren(current, life.childrenAges.length);
  current = loseSecondParent(current, rng.fork('parents'));
  const moved = current.iso3 !== life.iso3 || current.urban !== life.urban;
  const assets = updateAssets(ds, current, moved, rng.fork('housing'));
  current = { ...current, assets: assets.assets };
  if (assets.boughtHome) entries.push({ age: current.age, key: HOUSING_OWN_KEY, starred: true });
  current = { ...current, look: growLook(current, getCountry(ds, current.iso3).whoRegion, rng.fork('look')) };

  // 5c) Цели: мечта сбылась, цель года выполнена, вехи. Без жребиев.
  const achieved = achieveDream(current, ds);
  current = achieved.life;
  entries.push(...achieved.entries);
  const goal = settleGoal(current);
  current = goal.life;
  entries.push(...goal.entries);
  const stamped = stampMilestones(life, current);
  current = stamped.life;
  entries.push(...stamped.entries);

  // 8) Итог года пишется от прожитого возраста — до его увеличения.
  const summary = yearSummary(current, focus);

  // 6) Возраст и год учёбы; новый год — новая энергия, визиты обнуляются.
  current = {
    ...current,
    age: current.age + 1,
    schoolYearsDone: current.inSchool ? current.schoolYearsDone + 1 : current.schoolYearsDone,
    visits: {},
  };
  current = { ...current, energy: energyFor(current) };

  // 6a) Мечта предлагается в DREAM_AGE; цель года — новая, если прежняя выполнена или её не было.
  if (current.age === DREAM_AGE && current.dream === null && !input.noDream) {
    current = { ...current, pendingDream: offerDreams(current, ds, rng.fork('dream')) };
  }
  if (current.yearGoal === null || goal.done) current = { ...current, yearGoal: yearGoalFor(current, ds, rng.fork('goal')) };

  // 7) Решение нового возраста.
  const pending = dueDecision(ds, current, rng.fork(`decision/${current.age}`));
  current = { ...current, pendingDecision: pending };

  entries.push(summary);
  return finish(current, entries);
}
